import { LitElement, css, html, nothing } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import monkeySvg from './../../../assets/logo/monkey.svg';

import '@awesome.me/webawesome/dist/components/button/button.js';
import { resolveRouterPath } from '../../../router';

interface SectionNavigationEventDetail {
  sectionId: string;
}

@customElement('app-header')
export class AppHeader extends LitElement {
  @property({ type: String }) title = 'RIDE FOR THE WILD';
  @property({ type: Boolean }) enableBack: boolean = false;

  static styles = css`
    header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-sizing: border-box;
      background: #27251f;
      color: var(--app-color-on-primary);
      padding: 0 max(1.25rem, calc((100vw - 1200px) / 2));

      position: sticky;
      top: 0;
      z-index: 1000;
      left: env(titlebar-area-x, 0);
      height: env(titlebar-area-height, 7.5rem);
      width: env(titlebar-area-width, 100%);
      -webkit-app-region: drag;
    }

    header h1 {
      margin-top: 0;
      margin-bottom: 0;
      font-size: clamp(1rem, 1.45vw, 1.5rem);
      font-weight: bold;
      letter-spacing: 0.04em;
      white-space: nowrap;
    }

    nav {
      display: flex;
      align-items: center;
      gap: clamp(1rem, 2.2vw, 2.5rem);
      margin-left: auto;
      margin-right: 1rem;
      -webkit-app-region: no-drag;
    }

    nav a {
      color: var(--app-color-on-primary);
      text-decoration: none;
      font-size: clamp(0.9rem, 1.1vw, 1.125rem);
      opacity: 0.8;
    }

    nav a:hover {
      opacity: 1;
    }

    nav a[aria-current='location'] {
      opacity: 1;
      font-weight: 600;
    }

    #back-button-block {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      -webkit-app-region: no-drag;
    }

    #monkey-logo {
      height: clamp(2rem, 2.5vw, 2.75rem);
      width: clamp(2rem, 2.5vw, 2.75rem);
      object-fit: contain;
    }

    wa-button {
      --wa-form-control-height: 3.75rem;
      --wa-button-border-radius: 0.75rem;
      font-size: 1rem;
      font-weight: 700;
      -webkit-app-region: no-drag;
    }

    .mobile-order-button {
      display: none;
      --wa-color-fill-loud: transparent;
      --wa-color-neutral-fill-loud: transparent;
      --wa-button-background-color: transparent;
      --wa-button-border-color: transparent;
      --wa-button-hover-background-color: transparent;
      --wa-button-hover-border-color: transparent;
      --wa-button-color: var(--app-color-on-primary);
      --wa-button-hover-color: var(--app-color-on-primary);
    }

    .title-mobile {
      display: none;
    }

    @media (max-width: 900px) {
      header {
        justify-content: flex-start;
        height: 4.5rem;
        padding: 0 1.25rem;
      }

      nav {
        display: none;
      }

      wa-button {
        --wa-form-control-height: 2.75rem;
        font-size: 0.875rem;
      }

      .mobile-order-button {
        display: inline-flex;
        margin-left: auto;
        margin-right: 0.45rem;
      }

      header h1 {
        font-size: 0.95rem;
      }

      .title-desktop {
        display: none;
      }

      .title-mobile {
        display: inline;
      }

      #back-button-block {
        gap: 0.5rem;
      }

      #monkey-logo {
        height: 2rem;
        width: 2rem;
      }
    }

    @media (max-width: 380px) {
      header {
        padding: 0 0.75rem;
      }

      wa-button {
        --wa-form-control-height: 2.5rem;
      }
    }
  `;

  private handleSectionNavigation(event: MouseEvent, sectionId: string): void {
    event.preventDefault();
    window.location.hash = sectionId;
    this.dispatchEvent(
      new CustomEvent<SectionNavigationEventDetail>('section-navigation', {
        bubbles: true,
        composed: true,
        detail: { sectionId },
      })
    );
  }

  private handleOrderNavigation(event: MouseEvent): void {
    event.preventDefault();
    this.dispatchEvent(
      new CustomEvent('order-navigation', {
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    const homePath = resolveRouterPath();
    const currentSection = window.location.hash.slice(1);
    return html`
      <header>
        <div id="back-button-block">
          <img id="monkey-logo" src="${monkeySvg}" alt="Monkey logo" />
          ${this.enableBack
            ? html`<wa-button size="s" href="${homePath}"> Back </wa-button>`
            : null}

          <h1>
            <span class="title-desktop">${this.title}</span>
            <span class="title-mobile">R.F.T.W.</span>
          </h1>
        </div>

        <nav>
          <a
            href="${resolveRouterPath('#mission')}"
            aria-current="${currentSection === 'mission'
              ? 'location'
              : nothing}"
            @click="${(event: MouseEvent) =>
              this.handleSectionNavigation(event, 'mission')}"
            >Misión</a
          >
          <a
            href="${resolveRouterPath('#iar-nosara')}"
            aria-current="${currentSection === 'iar-nosara'
              ? 'location'
              : nothing}"
            @click="${(event: MouseEvent) =>
              this.handleSectionNavigation(event, 'iar-nosara')}"
            >IAR Nosara</a
          >
          <a
            href="${resolveRouterPath('#participar')}"
            aria-current="${currentSection === 'participar'
              ? 'location'
              : nothing}"
            @click="${(event: MouseEvent) =>
              this.handleSectionNavigation(event, 'participar')}"
            >Participar</a
          >
          <a href="#orden" @click=${this.handleOrderNavigation}>Ver orden</a>
        </nav>
        <wa-button
          class="mobile-order-button"
          variant="neutral"
          aria-label="Ver orden"
          @click=${this.handleOrderNavigation}
          >Ver orden</wa-button
        >
        <wa-button
          variant="brand"
          href="${resolveRouterPath('#participar')}"
          @click="${(event: MouseEvent) =>
            this.handleSectionNavigation(event, 'participar')}"
          >Inscribirse</wa-button
        >
      </header>
    `;
  }
}

